import type { VisibilityThreshold, ScrollPosition, ViewportDimensions } from "./types";

/**
 * Calculate if a component is visible based on its layout and the current scroll position
 */
export function calculateVisibility(
  layout: { x: number; y: number; width: number; height: number },
  scrollPosition: ScrollPosition,
  viewportDimensions: ViewportDimensions,
  threshold: VisibilityThreshold = { percentage: 0 },
): {
  isVisible: boolean;
  visibilityRatio: number;
  intersectionRect: { x: number; y: number; width: number; height: number };
} {
  const { x: componentX, y: componentY, width: componentWidth, height: componentHeight } = layout;
  const { x: scrollX, y: scrollY } = scrollPosition;
  const { width: viewportWidth, height: viewportHeight } = viewportDimensions;

  // Calculate component bounds in the scrollable area
  const componentLeft = componentX;
  const componentTop = componentY;
  const componentRight = componentX + componentWidth;
  const componentBottom = componentY + componentHeight;

  // Calculate viewport bounds
  const viewportLeft = scrollX;
  const viewportTop = scrollY;
  const viewportRight = scrollX + viewportWidth;
  const viewportBottom = scrollY + viewportHeight;

  // Calculate intersection
  const intersectionLeft = Math.max(componentLeft, viewportLeft);
  const intersectionTop = Math.max(componentTop, viewportTop);
  const intersectionRight = Math.min(componentRight, viewportRight);
  const intersectionBottom = Math.min(componentBottom, viewportBottom);

  // Check if there's any intersection
  const hasIntersection =
    intersectionLeft < intersectionRight && intersectionTop < intersectionBottom;

  if (!hasIntersection) {
    return {
      isVisible: false,
      visibilityRatio: 0,
      intersectionRect: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    };
  }

  // Calculate visible area
  const intersectionWidth = intersectionRight - intersectionLeft;
  const intersectionHeight = intersectionBottom - intersectionTop;
  const intersectionArea = intersectionWidth * intersectionHeight;
  const componentArea = componentWidth * componentHeight;
  const visibilityRatio = componentArea > 0 ? intersectionArea / componentArea : 0;

  // Check if visibility meets threshold requirements
  let isVisible = false;

  if (threshold.percentage !== undefined) {
    isVisible = visibilityRatio >= threshold.percentage;
  }

  if (threshold.pixels !== undefined) {
    const visiblePixels = intersectionArea;
    isVisible = isVisible || visiblePixels >= threshold.pixels;
  }

  // If no threshold is specified, consider visible if any part is visible
  if (threshold.percentage === undefined && threshold.pixels === undefined) {
    isVisible = visibilityRatio > 0;
  }

  return {
    isVisible,
    visibilityRatio,
    intersectionRect: {
      x: intersectionLeft,
      y: intersectionTop,
      width: intersectionWidth,
      height: intersectionHeight,
    },
  };
}

/**
 * Generate a unique ID for tracking components
 */
export function generateTrackingId(): string {
  return `visibility-tracker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
